import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { ASSETS } from "@/assets";
import { DISCORD_URL } from "@/constants/links";

const LINKS = [
  { label: "Domů", href: "#top", testid: "nav-home-link" },
  { label: "O Projektu", to: "/o-projektu", testid: "nav-about-link" },
  { label: "Lore", to: "/lore", testid: "nav-lore-link" },
  { label: "Pravidla", to: "/pravidla", testid: "nav-rules-link" },
  { label: "Jak hrát", to: "/jak-hrat", testid: "nav-howto-link" },
];

const Brand = ({ onGoHome }) => (
  <a href="#top" onClick={onGoHome} className="flex items-center" data-testid="brand-logo">
    <img src={ASSETS.logo} alt="Draconia RP" className="h-[54px] w-auto sm:h-[60px]" />
  </a>
);

export const Navbar = ({ onCreateAccount }) => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // "#top" links only make sense on the landing page itself (Lenis smooth-scrolls
  // there); on any other route (e.g. /ucet) they must navigate back home instead.
  const handleTopLinkClick = (e) => {
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/");
    }
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#141417]/90 backdrop-blur-xl"
      data-testid="navbar"
    >
      <nav className="mx-auto flex h-20 max-w-[1584px] items-center justify-between px-6">
        <Brand onGoHome={handleTopLinkClick} />

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => {
            const isActive = l.to
              ? location.pathname === l.to
              : l.href === "#top" && location.pathname === "/";
            const className = `font-display text-sm font-500 uppercase tracking-[0.14em] transition-colors duration-200 hover:text-[#F0801F] ${
              isActive ? "text-[#F0801F]" : "text-white/70"
            }`;
            if (l.to) {
              return (
                <Link key={l.to} to={l.to} data-testid={l.testid} className={className}>
                  {l.label}
                </Link>
              );
            }
            return (
              <a
                key={l.href}
                href={l.href}
                onClick={l.href === "#top" ? handleTopLinkClick : undefined}
                data-testid={l.testid}
                className={className}
              >
                {l.label}
              </a>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-discord-btn"
            className="hidden items-center gap-2 rounded-md bg-[#F0801F] px-5 py-3 font-display text-xs font-600 uppercase tracking-[0.14em] text-[#141417] transition-colors hover:bg-[#ff9438] sm:inline-flex"
          >
            <i className="fa-brands fa-discord text-sm" />
            Discord
          </a>
          {user ? (
            <Link
              to="/ucet"
              data-testid="nav-account-btn"
              className="hidden items-center gap-2 rounded-md border border-white/20 px-5 py-3 font-display text-xs font-600 uppercase tracking-[0.14em] text-white transition-colors hover:border-[#F0801F] hover:text-[#F0801F] sm:inline-flex"
            >
              <i className="fa-solid fa-user text-sm" />
              Můj účet
            </Link>
          ) : (
            <>
              <button
                onClick={onCreateAccount}
                data-testid="nav-register-btn"
                className="hidden items-center gap-2 rounded-md bg-[#F0801F] px-5 py-3 font-display text-xs font-600 uppercase tracking-[0.14em] text-[#141417] transition-colors hover:bg-[#ff9438] sm:inline-flex"
              >
                <i className="fa-solid fa-user-plus text-sm" />
                Registrace
              </button>
              <Link
                to="/ucet"
                data-testid="nav-login-btn"
                className="hidden items-center gap-2 rounded-md border border-white/20 px-5 py-3 font-display text-xs font-600 uppercase tracking-[0.14em] text-white transition-colors hover:border-[#F0801F] hover:text-[#F0801F] sm:inline-flex"
              >
                <i className="fa-solid fa-right-to-bracket text-sm" />
                Přihlásit
              </Link>
            </>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="grid h-11 w-11 place-items-center rounded-md border border-white/10 bg-white/5 text-white lg:hidden"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-b border-white/10 bg-[#141417]/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {LINKS.map((l) => {
                const className =
                  "rounded-md px-4 py-3 font-display text-sm uppercase tracking-[0.14em] text-white/80 transition-colors hover:bg-white/5 hover:text-[#F0801F]";
                if (l.to) {
                  return (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`${l.testid}-mobile`}
                      className={className}
                    >
                      {l.label}
                    </Link>
                  );
                }
                return (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={(e) => {
                      setOpen(false);
                      if (l.href === "#top") handleTopLinkClick(e);
                    }}
                    data-testid={`${l.testid}-mobile`}
                    className={className}
                  >
                    {l.label}
                  </a>
                );
              })}
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-discord-btn-mobile"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-md bg-[#F0801F] px-4 py-3 text-left font-display text-sm font-600 uppercase tracking-[0.14em] text-[#141417]"
              >
                Discord
              </a>
              {user ? (
                <Link
                  to="/ucet"
                  onClick={() => setOpen(false)}
                  data-testid="nav-account-btn-mobile"
                  className="rounded-md border border-white/20 px-4 py-3 text-left font-display text-sm font-600 uppercase tracking-[0.14em] text-white"
                >
                  Můj účet
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onCreateAccount();
                    }}
                    data-testid="nav-register-btn-mobile"
                    className="rounded-md bg-[#F0801F] px-4 py-3 text-left font-display text-sm font-600 uppercase tracking-[0.14em] text-[#141417]"
                  >
                    Registrace
                  </button>
                  <Link
                    to="/ucet"
                    onClick={() => setOpen(false)}
                    data-testid="nav-login-btn-mobile"
                    className="rounded-md border border-white/20 px-4 py-3 text-left font-display text-sm font-600 uppercase tracking-[0.14em] text-white"
                  >
                    Přihlásit
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
